import React from 'react'
import {
  UseFormRegister,
  UseFormHandleSubmit,
  useFieldArray,
  Control,
} from 'react-hook-form'
import { SlideFaq, ConcernItem, FaqItem, FaqFormData } from '../hooks/faq/types'
import { SlideContainer } from '../components/slide_container'
import { SlideHeader } from '../components/slide_header'
import {
  AiRegenerateSection,
  AiInstructionFormData,
} from '../components/ai_regenerate_section'
import FlatButton from '../../../components/buttons/flat_button'
import { TextareaInput } from '../../../components/form'
import logoGray from '../../../images/logo_gray.png'

interface FaqSlideProps {
  slideFaq: SlideFaq | null
  isEditing: boolean
  isUpdating: boolean
  isDirty: boolean
  register: UseFormRegister<FaqFormData>
  handleSubmit: UseFormHandleSubmit<FaqFormData>
  control: Control<FaqFormData>
  registerAi: UseFormRegister<AiInstructionFormData>
  handleSubmitAi: UseFormHandleSubmit<AiInstructionFormData>
  aiInstruction: string
  onFormSubmit: (data: FaqFormData) => Promise<void>
  onAiRegenerate: (data: AiInstructionFormData) => Promise<void>
}

export const FaqSlide = React.forwardRef<HTMLDivElement, FaqSlideProps>(
  (
    {
      slideFaq,
      isEditing,
      isUpdating,
      isDirty,
      register,
      handleSubmit,
      control,
      registerAi,
      handleSubmitAi,
      aiInstruction,
      onFormSubmit,
      onAiRegenerate,
    },
    ref
  ) => {
    const {
      fields: concernFields,
      append: appendConcern,
      remove: removeConcern,
    } = useFieldArray({
      control,
      name: 'concerns',
    })

    const {
      fields: faqFields,
      append: appendFaq,
      remove: removeFaq,
    } = useFieldArray({
      control,
      name: 'faqs',
    })

    if (!slideFaq) {
      return (
        <div className="flex items-center justify-center h-64">
          <p>FAQ・懸念点まとめが見つかりません</p>
        </div>
      )
    }

    return (
      <div ref={ref}>
        <SlideContainer>
          <SlideHeader
            title="5. FAQ・懸念点まとめ"
            isEditing={isEditing}
            isUpdating={isUpdating}
            isDirty={isDirty}
            onUpdate={handleSubmit(onFormSubmit)}
          />

          <div className="slide-content px-8 py-6">
            <div className="grid grid-cols-2 gap-8">
              <div>
                <p className="text-lg font-bold mb-4">懸念事項</p>

                {isEditing ? (
                  <div className="space-y-4">
                    {concernFields.map((field, index) => (
                      <div
                        key={field.id}
                        className="border border-main-gray rounded-lg p-4 space-y-3"
                      >
                        <div className="flex items-start">
                          <span className="font-bold flex-shrink-0 mr-2">
                            懸念点：
                          </span>
                          <div className="flex-1">
                            <TextareaInput
                              register={register(
                                `concerns.${index}.label` as const
                              )}
                              placeholder="懸念点を入力"
                              rows={2}
                            />
                          </div>
                        </div>
                        <div className="flex items-start">
                          <span className="font-bold flex-shrink-0 mr-2">
                            対応策：
                          </span>
                          <div className="flex-1">
                            <TextareaInput
                              register={register(
                                `concerns.${index}.value` as const
                              )}
                              placeholder="対応策を入力"
                              rows={2}
                            />
                          </div>
                        </div>
                        <div className="flex justify-end">
                          <FlatButton
                            type="button"
                            onClick={() => removeConcern(index)}
                            disabled={concernFields.length <= 1}
                            className="!bg-main-red w-20"
                          >
                            削除
                          </FlatButton>
                        </div>
                      </div>
                    ))}
                    <FlatButton
                      onClick={() => appendConcern({ label: '', value: '' })}
                    >
                      懸念点を追加
                    </FlatButton>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {(() => {
                      try {
                        const concerns = JSON.parse(
                          slideFaq.concerns || '[]'
                        ) as ConcernItem[]
                        return concerns.map((concern, index) => (
                          <div key={index} className="mb-6">
                            <div className="flex mb-1">
                              <span className="font-bold flex-shrink-0 mr-1">
                                懸念点：
                              </span>
                              <span>{concern.label}</span>
                            </div>
                            <div className="flex">
                              <span className="font-bold flex-shrink-0 mr-1">
                                対応策：
                              </span>
                              <span>{concern.value}</span>
                            </div>
                          </div>
                        ))
                      } catch (error) {
                        console.error('懸念点データのパースエラー:', error)
                        return (
                          <p className="text-main-red">
                            懸念点データの読み込みに失敗しました。
                          </p>
                        )
                      }
                    })()}
                  </div>
                )}
              </div>

              <div>
                <p className="text-lg font-bold mb-4">投資家・上司向け Q&A</p>
                {isEditing ? (
                  <div className="space-y-4">
                    {faqFields.map((field, index) => (
                      <div
                        key={field.id}
                        className="border border-main-gray rounded-lg p-4 space-y-3"
                      >
                        <div className="flex items-start">
                          <span className="font-bold flex-shrink-0 mr-2">
                            Q.
                          </span>
                          <div className="flex-1">
                            <TextareaInput
                              register={register(
                                `faqs.${index}.label` as const
                              )}
                              placeholder="質問を入力"
                              rows={2}
                            />
                          </div>
                        </div>
                        <div className="flex items-start">
                          <span className="font-bold flex-shrink-0 mr-2">
                            A.
                          </span>
                          <div className="flex-1">
                            <TextareaInput
                              register={register(
                                `faqs.${index}.value` as const
                              )}
                              placeholder="回答を入力"
                              rows={2}
                            />
                          </div>
                        </div>
                        <div className="flex justify-end">
                          <FlatButton
                            type="button"
                            onClick={() => removeFaq(index)}
                            disabled={faqFields.length <= 1}
                            className="!bg-main-red w-20"
                          >
                            削除
                          </FlatButton>
                        </div>
                      </div>
                    ))}
                    <FlatButton
                      onClick={() => appendFaq({ label: '', value: '' })}
                    >
                      Q&Aを追加
                    </FlatButton>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {(() => {
                      try {
                        const faqs = JSON.parse(
                          slideFaq.faqs || '[]'
                        ) as FaqItem[]
                        return faqs.map((faq, index) => (
                          <div key={index} className="mb-6">
                            <div className="flex mb-1">
                              <span className="font-bold flex-shrink-0 mr-1">
                                Q.
                              </span>
                              <span>{faq.label}</span>
                            </div>
                            <div className="flex">
                              <span className="font-bold flex-shrink-0 mr-1">
                                A.
                              </span>
                              <span>{faq.value}</span>
                            </div>
                          </div>
                        ))
                      } catch (error) {
                        console.error('FAQデータのパースエラー:', error)
                        return (
                          <p className="text-main-red">
                            FAQデータの読み込みに失敗しました。
                          </p>
                        )
                      }
                    })()}
                  </div>
                )}
              </div>
            </div>
            {isEditing && (
              <div className="mt-8">
                <AiRegenerateSection
                  register={registerAi}
                  handleSubmit={handleSubmitAi}
                  aiInstruction={aiInstruction}
                  isUpdating={isUpdating}
                  onSubmit={onAiRegenerate}
                />
              </div>
            )}
            <div className="slide-footer mt-20">
              <p className="text-xs text-main-darkgray text-center mb-2">
                この企画書は「SDGs 共創
                Lodge」の共創アイディア生成AIで生成しました。
              </p>
              <img
                src={logoGray}
                alt="SDGs共創Lodge Logo"
                className="absolute bottom-2 right-4 w-20 h-auto"
              />
            </div>
          </div>
        </SlideContainer>
      </div>
    )
  }
)

FaqSlide.displayName = 'FaqSlide'
